// SPEC-31 · the app's language, apart from React: which one the phone
// starts in, what this device remembers, and the copy each one reads.
//
// The device remembers a language she chose, never one it guessed: until
// she taps a segment, the app follows the phone. Storage can be missing or
// throw (a private window, blocked site data); then the app follows the
// phone, and a choice lasts as long as the page.

import { LANGUAGES } from "../schema/types";
import type { Language } from "../schema/types";
import { en } from "./en";
import { fr } from "./fr";
import { copy } from "./pt-PT";
import type { ShellCopy, ShellSection } from "./shell";

export type { Language };

/** Every word of the app in one language: PT's, with the language's shell in place of PT's. */
export type Copy = Omit<typeof copy, ShellSection> & ShellCopy;

/** The language a page is in, as `<html lang>` and a `lang` attribute name it. */
export const HTML_LANG: Readonly<Record<Language, string>> = { pt: "pt-PT", fr: "fr", en: "en" };

/** The language she chose on this device. */
const LANGUAGE_KEY = "pyf.language";
/** Set while a choice waits to reach her account: the last save failed. */
const UNSAVED_KEY = "pyf.language.unsaved";

export const isLanguage = (value: unknown): value is Language => LANGUAGES.some((language) => language === value);

/**
 * The phone's language when the app has it, otherwise PT: the primary
 * subtag of the first language the phone lists (« fr-CA » reads FR,
 * « es-ES » reads PT, whatever comes after it).
 */
export function deviceLanguage(languages: readonly string[] = phoneLanguages()): Language {
  const primary = (languages[0] ?? "").split("-")[0]?.toLowerCase();
  return isLanguage(primary) ? primary : "pt";
}

function phoneLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  if (navigator.languages && navigator.languages.length > 0) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Unavailable storage costs the memory only: the language still applies on this page.
  }
}

/** At first opening, the phone's; once she has chosen on this device, hers. */
export function initialLanguage(): Language {
  const stored = read(LANGUAGE_KEY);
  return isLanguage(stored) ? stored : deviceLanguage();
}

export function storeLanguage(language: Language): void {
  write(LANGUAGE_KEY, language);
}

/** Whether a choice made here has not reached her account yet. */
export const unsavedChoice = (): boolean => read(UNSAVED_KEY) !== null;

export function markUnsaved(unsaved: boolean): void {
  write(UNSAVED_KEY, unsaved ? "1" : null);
}

const COPIES: Readonly<Record<Language, Copy>> = {
  pt: copy,
  fr: { ...copy, ...fr },
  en: { ...copy, ...en },
};

/** PT's copy for PT; for another language, PT's with that language's shell sections in place of PT's. */
export const copyFor = (language: Language): Copy => COPIES[language];

/** « Hoje · qui 23 out », with the language's abbreviated day and month. */
export function todayLine(words: Copy, date: Date): string {
  const { today, weekdays, months } = words.assessments;
  return today(weekdays[date.getDay()] ?? "", date.getDate(), months[date.getMonth()] ?? "");
}
